import { FileType } from "../graphql/generated";
import { DataRoomFilters } from "../routes/data_room";
import { sortByIndex } from "./sortByIndex";

/** Minimal shape shared across AccessMatrix / AllFolders / AllDataRoomFolders* queries */
type HierarchyFile = {
  id: string;
  name: string;
  fileType: FileType;
  folderId?: string;
  updatedAt?: number;
  index?: { sortIndex: number } | null;
  parentFolders?: { id: string; index?: { sortIndex: number } | null }[];
  createdBy?: { id: string };
};

type HierarchyFolder = {
  id: string;
  name: string;
  index?: { id?: string; sortIndex: number } | null;
  parentFolders: { id: string; index?: { sortIndex: number } | null }[];
  files: HierarchyFile[];
  createdBy?: { id: string };
  updatedAt?: number;
  permissions?: { id: string; name: string; description?: string | null }[];
};

export type FolderWrapper = {
  folder: HierarchyFolder;
  folders: FolderWrapper[];
  files: HierarchyFile[];
};

export const buildFolderHierarchy = (
  foldersInput: readonly HierarchyFolder[],
  indexingEnabled: boolean = false,
  filterFiles: string[] = [],
  filterFolders: string[] = [],
  filters?: DataRoomFilters,
): FolderWrapper[] => {
  const folders = foldersInput as HierarchyFolder[];

  const parentFoldersMap: { [key: string]: HierarchyFolder[] } = {};

  folders.forEach((folder) => {
    if (folder.parentFolders.length > 1) {
      if (!parentFoldersMap[folder.parentFolders[1].id]) {
        parentFoldersMap[folder.parentFolders[1].id] = [];
      }

      parentFoldersMap[folder.parentFolders[1].id].push(folder);
    }
  });

  function buildFolderWrapper(folder: HierarchyFolder): FolderWrapper {
    return {
      folder: folder,
      folders: parentFoldersMap[folder.id]
        ? parentFoldersMap[folder.id]
            .filter((f) => {
              if (filterFolders.length > 0) {
                return filterFolders.includes(f.id);
              }
              return true;
            })
            .sort((a, b) => sortByIndex(a, b, indexingEnabled))
            .map((f) => buildFolderWrapper(f))
        : [],
      files: folder.files
        .filter((f) => {
          if (filterFiles.length > 0) {
            return filterFiles.includes(f.id);
          }

          if (filters?.type && filters.type !== f.fileType) {
            return false;
          }

          if (
            filters?.createdBy &&
            f.createdBy &&
            filters.createdBy.id !== f.createdBy.id
          ) {
            return false;
          }

          if (
            filters?.lastUpdated &&
            f.updatedAt !== undefined &&
            f.updatedAt < filters.lastUpdated
          ) {
            return false;
          }

          return true;
        })
        .sort((a, b) => {
          if (indexingEnabled) {
            return (a.index?.sortIndex ?? 0) - (b.index?.sortIndex ?? 0);
          }
          return a.name.localeCompare(b.name);
        }),
    };
  }

  return folders
    .filter((f) => f.parentFolders.length === 0)
    .map((f) => buildFolderWrapper(f));
};
